export const saturnTree = {
  id: 'saturn',
  label: 'Saturn',
  type: 'Gas giant',
  summary: 'The ringed planet: second largest, least dense, and arguably the most photogenic body in the solar system.',
  payload: [
    {
      type: 'description',
      text: 'Saturn is famous for its spectacular ring system, a disk of ice and rock particles ranging from dust grains to house-sized chunks. Like Jupiter, it\'s a gas giant with no solid surface: hydrogen and helium transitioning through liquid and metallic states. Its moon Titan has rivers and lakes of liquid methane, and Enceladus vents water from a subsurface ocean.'
    },
    {
      type: 'stats',
      label: 'At a glance',
      entries: [
        { label: 'Diameter',          value: '116,460 km (9.14 × Earth)' },
        { label: 'Mass',              value: '5.683 × 10²⁶ kg (95 × Earth)' },
        { label: 'Distance from Sun', value: '1,432.0 M km (9.57 AU)' },
        { label: 'Orbital period',    value: '29.45 Earth years' },
        { label: 'Day length',        value: '10h 33m' },
        { label: 'Mean density',      value: '0.687 g/cm³ (would float in water)' },
        { label: 'Moons',             value: '293 confirmed (most in solar system, JPL, October 2026)' },
        { label: 'Temp at 1 bar',     value: '−139 °C' }
      ]
    }
  ],
  children: [
    {
      id: 'vital-stats',
      label: 'Vital statistics',
      summary: 'Size, density, gravity, magnetic field',
      payload: [
        {
          type: 'stats',
          label: 'Physical',
          entries: [
            { label: 'Equatorial radius', value: '60,268 km' },
            { label: 'Mean density',      value: '0.687 g/cm³' },
            { label: 'Surface gravity',   value: '11.19 m/s² (1.141 g)' },
            { label: 'Escape velocity',   value: '35.5 km/s' },
            { label: 'Oblateness',        value: '0.098 (most oblate planet)' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'Saturn\'s mean density is only 0.687 g/cm³, less than water. If you had a bathtub big enough, it would float.'
        }
      ]
    },
    {
      id: 'rings',
      label: 'Ring system',
      summary: 'Seven named rings of ice and rock, as thin as a sheet of paper relative to their width',
      payload: [
        {
          type: 'description',
          text: 'Saturn\'s main rings are about 274,000 km across, yet they are typically only about 10 m thick. They are ~90–95% water ice, with the remainder being rocky material. Gaps like the Cassini Division are carved by resonances with shepherd moons. Their origin is still debated: either primordial material that never coalesced into a moon, or the shattered remains of a moon destroyed within the last few hundred million years.'
        },
        {
          type: 'stats',
          label: 'Ring structure',
          entries: [
            { label: 'D ring',         value: '66,900 – 74,510 km (innermost)' },
            { label: 'C ring',         value: '74,658 – 92,000 km' },
            { label: 'B ring',         value: '92,000 – 117,580 km (brightest)' },
            { label: 'Cassini Division', value: '117,580 – 122,170 km (gap)' },
            { label: 'A ring',         value: '122,170 – 136,775 km' },
            { label: 'F ring',         value: '~140,180 km (shepherded)' },
            { label: 'G ring',         value: '166,000 – 175,000 km' },
            { label: 'E ring',         value: '180,000 – 480,000 km (from Enceladus)' }
          ]
        },
        {
          type: 'callout',
          tone: 'warn',
          text: 'The rings are slowly "raining" onto Saturn. At current rates they will disappear in 100 – 300 million years. We exist during a geologically brief window of ring visibility.'
        }
      ]
    },
    {
      id: 'moons',
      label: 'Major moons',
      summary: 'Titan, Enceladus, Iapetus, Mimas, and 289 more',
      children: [
        {
          id: 'titan',
          label: 'Titan',
          summary: 'Only moon with a substantial atmosphere, and hydrocarbon lakes on its surface',
          payload: [
            { type: 'description', text: 'Larger than Mercury. The only moon with a dense atmosphere (1.5 bar, mostly nitrogen). Surface temperature of −179 °C keeps methane liquid, so Titan has rivers, lakes, and seas of methane and ethane. Hydrology, erosion, and chemistry are unlike anywhere else.' },
            {
              type: 'stats',
              entries: [
                { label: 'Diameter',       value: '5,149 km (larger than Mercury)' },
                { label: 'Orbital period', value: '15.95 Earth days' },
                { label: 'Surface pressure', value: '1.45 bar' },
                { label: 'Surface temp',   value: '−179 °C' },
                { label: 'Atmosphere',     value: '95% N₂, 4.9% CH₄' }
              ]
            },
            { type: 'callout', tone: 'info', text: 'NASA\'s Dragonfly rotorcraft will launch in 2028 and arrive at Titan in 2034. It will be the first multi-rotor drone to fly on another world.' }
          ]
        },
        {
          id: 'enceladus',
          label: 'Enceladus',
          summary: 'Tiny icy moon with a global ocean and water-vapor geysers',
          payload: [
            { type: 'description', text: 'Only 504 km wide but fountains water from its south polar "tiger stripes", fissures in the ice. A subsurface ocean lies beneath a 20–25 km ice shell. Cassini flew through the plumes and detected organics, silica from hydrothermal activity, and molecular hydrogen. One of the best places in the solar system to search for life.' },
            {
              type: 'stats',
              entries: [
                { label: 'Diameter',       value: '504 km' },
                { label: 'Orbital period', value: '1.37 Earth days' },
                { label: 'Ice shell',      value: '20 – 25 km' },
                { label: 'Ocean',          value: 'Global, ~10 km deep' },
                { label: 'Plume height',   value: 'Up to 500 km' }
              ]
            }
          ]
        },
        {
          id: 'iapetus',
          label: 'Iapetus',
          summary: 'Two-toned moon with an equatorial ridge',
          payload: [
            { type: 'description', text: 'Has a leading hemisphere as dark as coal and a trailing hemisphere as bright as snow. Runs a strange 20 km high ridge along much of its equator. The dark material likely comes from a debris ring donated by the outer moon Phoebe.' },
            {
              type: 'stats',
              entries: [
                { label: 'Diameter',       value: '1,469 km' },
                { label: 'Orbital period', value: '79.3 Earth days' }
              ]
            }
          ]
        },
        {
          id: 'mimas',
          label: 'Mimas',
          summary: 'The "Death Star" moon',
          payload: [
            { type: 'description', text: 'Distinguished by Herschel, an impact crater 130 km across on a 396 km moon, nearly a third of the moon\'s own diameter. Recent research suggests Mimas may also harbor a young subsurface ocean.' },
            {
              type: 'stats',
              entries: [
                { label: 'Diameter',  value: '396 km' },
                { label: 'Herschel crater', value: '130 km wide (33% of moon)' }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'atmosphere',
      label: 'Atmosphere',
      summary: 'Hydrogen-helium, fast jets, hexagonal polar storm',
      payload: [
        {
          type: 'description',
          text: 'Less visually banded than Jupiter due to a haze layer above the cloud tops. Equatorial jet streams reach 1,800 km/h. The north pole hosts a persistent hexagonal jet stream ~30,000 km across, and in 2026 Hubble observations revealed a ten-sided wave circling the south pole as well.'
        },
        {
          type: 'composition-bars',
          entries: [
            { name: 'Hydrogen (H₂)',  percent: 96.3, color: '#88ccff' },
            { name: 'Helium (He)',    percent: 3.25, color: '#ffaaaa' },
            { name: 'Methane (CH₄)',  percent: 0.45, color: '#aa6644' },
            { name: 'Ammonia (NH₃)',  percent: 0.0125, color: '#c099ff' }
          ]
        }
      ]
    },
    {
      id: 'missions',
      label: 'Missions & exploration',
      summary: 'From Pioneer 11 to Cassini-Huygens and Dragonfly',
      payload: [
        {
          type: 'stats',
          entries: [
            { label: 'Pioneer 11',   value: '1979 flyby' },
            { label: 'Voyager 1',    value: '1980 flyby' },
            { label: 'Voyager 2',    value: '1981 flyby' },
            { label: 'Cassini',      value: '2004–2017, orbital mission' },
            { label: 'Huygens',      value: '2005, landed on Titan' },
            { label: 'Dragonfly',    value: 'NASA, launches 2028, arrives 2034' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: 'Cassini ended its mission with the "Grand Finale": 22 close dives between Saturn and its rings, before a final plunge into the atmosphere on September 15, 2017 to avoid contaminating potentially habitable moons.'
        }
      ]
    },
    {
      id: 'references',
      label: 'External references',
      summary: 'Where to dig deeper',
      payload: [
        {
          type: 'links',
          entries: [
            { label: 'NASA fact sheet',   url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/saturnfact.html' },
            { label: 'Cassini archive',   url: 'https://science.nasa.gov/mission/cassini/' },
            { label: 'Dragonfly',         url: 'https://dragonfly.jhuapl.edu/' },
            { label: 'NASA: Saturn facts', url: 'https://science.nasa.gov/saturn/facts/' },
            { label: 'NASA: length of a Saturn day', url: 'https://science.nasa.gov/solar-system/scientists-finally-know-what-time-it-is-on-saturn/' },
            { label: 'JPL: satellite discovery list', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
            { label: 'Wikipedia: Saturn', url: 'https://en.wikipedia.org/wiki/Saturn' }
          ]
        }
      ]
    }
  ]
};
